import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  Table,
  Tag,
  Button,
  Spin,
  Alert,
  Tooltip,
  message,
  Empty,
} from "antd";
import {
  EditOutlined,
  SendOutlined,
  SyncOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import customClientAxios from "../customAxios";
import {
  AdminNurtureTemplates,
  AdminNurtureTemplateConfig,
  AdminNurtureTemplateSubmit,
  AdminNurtureTemplateSyncStatus,
} from "../utils/ApiEndpoints";
import TemplateEditor from "./TemplateEditor";
import TestSendDialog from "./TestSendDialog";

// ── Sequence catalog ──────────────────────────────────────────────────────
// Mirrors backend services/nurture/sequenceMap.js SEQUENCES.
// Order is intentional — follows funnel progression.
const SEQUENCES = [
  { key: "prospect",            label: "Prospect",            description: "Day 1–30 onboarding push for new prospects (no signup yet)" },
  { key: "qualified",           label: "Qualified",           description: "After a contact clicks a nurture CTA · close the signup" },
  { key: "onboarded",           label: "Onboarded",           description: "Welcome + nudge to create brand page" },
  { key: "brand_page",          label: "Brand Page",          description: "Brand page exists · nudge to publish first event" },
  { key: "event_created",       label: "Event Created",       description: "Event live · drive first ticket sale" },
  { key: "first_sale",          label: "First Sale",          description: "First sale made · upgrade to paid plan" },
  { key: "purchased_retention", label: "Plan Purchased",      description: "Retention sequence for paying customers" },
];

const Page = styled.div`
  padding: 24px 32px;
  background: #f8fafc;
  min-height: 100vh;
`;

const Header = styled.div`
  margin-bottom: 24px;
  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px;
  }
  p {
    color: #64748b;
    margin: 0;
    font-size: 14px;
  }
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const StatCard = styled.div`
  background: white;
  padding: 14px 18px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  .label { font-size: 12px; color: #64748b; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
  .value { font-size: 22px; font-weight: 700; color: #1e293b; }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const BodyPreview = styled.div`
  font-size: 12px;
  color: #475569;
  line-height: 1.5;
  white-space: pre-wrap;
  max-width: 380px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const STATUS_COLOR = {
  draft: "default",
  pending: "processing",
  approved: "success",
  rejected: "error",
};

const STATUS_LABEL = {
  draft: "Draft",
  pending: "Pending Meta",
  approved: "Approved",
  rejected: "Rejected",
};

// ── Tab bar styles ────────────────────────────────────────────────────────

const TabsBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 18px;
  overflow-x: auto;
  scrollbar-width: thin;

  &::-webkit-scrollbar { height: 4px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }
`;

const Tab = styled.button`
  position: relative;
  padding: 10px 16px 11px;
  background: transparent;
  border: none;
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  color: ${(p) => (p.$active ? "#0f172a" : "#64748b")};
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: "";
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: -1px;
    height: 2px;
    background: ${(p) => (p.$active ? "linear-gradient(90deg, #6366f1, #8b5cf6)" : "transparent")};
    border-radius: 2px 2px 0 0;
  }

  &:hover { color: #0f172a; }

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: ${(p) => (p.$active ? "rgba(99,102,241,0.15)" : "#f1f5f9")};
    color: ${(p) => (p.$active ? "#4f46e5" : "#94a3b8")};
    font-size: 10px;
    font-weight: 700;
  }
`;

const SeqDescription = styled.div`
  font-size: 12px;
  color: #64748b;
  margin: -10px 0 16px;
  padding: 8px 12px;
  background: rgba(99,102,241,0.04);
  border-left: 3px solid #6366f1;
  border-radius: 0 6px 6px 0;
`;

export default function NurtureTemplates() {
  const [allRows, setAllRows] = useState([]); // ALL sequences from /templates
  const [activeSeq, setActiveSeq] = useState("prospect");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // step row being edited
  const [testing, setTesting] = useState(null); // step row being test-sent
  const [busyStepDay, setBusyStepDay] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // No ?sequenceKey → backend returns all sequences in one shot
      const { data } = await customClientAxios().get(AdminNurtureTemplates);
      setAllRows(data?.data || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error?.message || e.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Rows filtered to the active sequence tab
  const rows = allRows.filter((r) => (r.sequenceKey || "prospect") === activeSeq);

  // Per-sequence step counts for tab badges (only "approved" counts as ready)
  const seqCounts = SEQUENCES.reduce((acc, s) => {
    const items = allRows.filter((r) => (r.sequenceKey || "prospect") === s.key);
    acc[s.key] = {
      total: items.length,
      approved: items.filter((r) => r.template?.status === "approved").length,
    };
    return acc;
  }, {});

  // ── Step-config toggle (isActive) ─────────────────────────────────────────
  const toggleActive = async (row) => {
    setBusyStepDay(row.stepDay);
    try {
      await customClientAxios().patch(AdminNurtureTemplateConfig(row.stepDay, activeSeq), {
        isActive: !row.isActive,
      });
      message.success(`${activeSeq} D${row.stepDay} ${row.isActive ? "paused" : "activated"}`);
      await fetchAll();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || "Failed to toggle");
    } finally {
      setBusyStepDay(null);
    }
  };

  // ── Submit to msg91 for Meta approval ────────────────────────────────────
  const submitForApproval = async (row) => {
    setBusyStepDay(row.stepDay);
    try {
      await customClientAxios().post(AdminNurtureTemplateSubmit(row.stepDay, activeSeq));
      message.success(`${activeSeq} D${row.stepDay} submitted to Meta for approval`);
      await fetchAll();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e?.response?.data?.message || "Submit failed");
    } finally {
      setBusyStepDay(null);
    }
  };

  // ── Sync status from msg91 ────────────────────────────────────────────────
  const syncStatus = async (row) => {
    setBusyStepDay(row.stepDay);
    try {
      const { data } = await customClientAxios().post(AdminNurtureTemplateSyncStatus(row.stepDay, activeSeq));
      const newStatus = data?.data?.template?.status;
      message.success(`Synced — status: ${newStatus || "unknown"}`);
      await fetchAll();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || "Sync failed");
    } finally {
      setBusyStepDay(null);
    }
  };

  // ── Stats summary ─────────────────────────────────────────────────────────
  const stats = {
    total: rows.length,
    active: rows.filter((r) => r.isActive).length,
    approved: rows.filter((r) => r.template?.status === "approved").length,
    draft: rows.filter((r) => r.template?.status === "draft").length,
    pending: rows.filter((r) => r.template?.status === "pending").length,
    rejected: rows.filter((r) => r.template?.status === "rejected").length,
  };

  const columns = [
    {
      title: "Step",
      dataIndex: "stepDay",
      key: "stepDay",
      width: 70,
      render: (d) => (
        <Tag style={{ fontWeight: 600, fontSize: 13 }} color="purple">D{d}</Tag>
      ),
    },
    {
      title: "Template",
      key: "template",
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1e293b" }}>
            {row.template?.displayName || row.template?.name || "—"}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace" }}>
            {row.template?.name}
          </div>
        </div>
      ),
    },
    {
      title: "Body Preview",
      key: "body",
      render: (_, row) => {
        const body = (row.template?.components || []).find((c) => c.type === "BODY")?.text || "—";
        return <BodyPreview>{body}</BodyPreview>;
      },
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, row) => {
        const s = row.template?.status || "draft";
        return (
          <div>
            <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
            {s === "rejected" && row.template?.rejectionReason && (
              <Tooltip title={row.template.rejectionReason}>
                <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>
                  See reason
                </div>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Active",
      key: "active",
      width: 80,
      render: (_, row) => (
        <Button
          type="text"
          size="small"
          icon={row.isActive ? <PlayCircleOutlined style={{ color: "#10b981" }} /> : <PauseCircleOutlined style={{ color: "#94a3b8" }} />}
          onClick={() => toggleActive(row)}
          loading={busyStepDay === row.stepDay}
        >
          {row.isActive ? "On" : "Off"}
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 320,
      render: (_, row) => {
        const status = row.template?.status;
        const canEditBody = status === "draft" || status === "rejected";
        // Approved templates can still be opened so admins can attach a
        // header image — uploading flips the template back to draft and the
        // Submit button then re-requests Meta approval.
        const canOpenEditor = status !== "pending";
        const canSubmit = status === "draft" || status === "rejected";
        const canTest = status === "approved";
        const editTooltip =
          status === "pending"
            ? "Editing disabled while Meta is reviewing"
            : canEditBody
            ? "Edit body, button, header image"
            : "Body is read-only on approved templates — but you can attach a header image (the template will return to draft for re-approval)";
        return (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <Tooltip title={editTooltip}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => setEditing(row)}
                disabled={!canOpenEditor}
              >
                Edit
              </Button>
            </Tooltip>
            <Tooltip title={canSubmit ? "Submit to msg91 for Meta approval" : `Already ${status}`}>
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                onClick={() => submitForApproval(row)}
                loading={busyStepDay === row.stepDay}
                disabled={!canSubmit}
              >
                Submit
              </Button>
            </Tooltip>
            <Tooltip title="Poll msg91 for latest approval status">
              <Button
                size="small"
                icon={<SyncOutlined />}
                onClick={() => syncStatus(row)}
                loading={busyStepDay === row.stepDay}
                disabled={status === "draft"}
              >
                Sync
              </Button>
            </Tooltip>
            <Tooltip title={canTest ? "Send a test message to your phone" : "Template must be approved first"}>
              <Button
                size="small"
                icon={<ExperimentOutlined />}
                onClick={() => setTesting(row)}
                disabled={!canTest}
              >
                Test
              </Button>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const activeSeqMeta = SEQUENCES.find((s) => s.key === activeSeq);

  return (
    <Page>
      <Header>
        <h1>Sequence Manager</h1>
        <p>
          7 WhatsApp nurture sequences across the full funnel — each with 7 steps.
          Switch tabs to manage any sequence. Edit body → Submit → msg91 sends to Meta for approval.
        </p>
      </Header>

      {/* Sequence tabs */}
      <TabsBar>
        {SEQUENCES.map((s) => {
          const c = seqCounts[s.key] || { total: 0, approved: 0 };
          const isActive = activeSeq === s.key;
          return (
            <Tab
              key={s.key}
              $active={isActive}
              onClick={() => setActiveSeq(s.key)}
            >
              <span>{s.label}</span>
              <span className="badge">
                {c.approved}/{c.total}
              </span>
            </Tab>
          );
        })}
      </TabsBar>

      {activeSeqMeta && (
        <SeqDescription>{activeSeqMeta.description}</SeqDescription>
      )}

      <StatsBar>
        <StatCard><div className="label">Total Steps</div><div className="value">{stats.total}</div></StatCard>
        <StatCard><div className="label">Active</div><div className="value">{stats.active}</div></StatCard>
        <StatCard><div className="label">Approved</div><div className="value" style={{ color: "#10b981" }}>{stats.approved}</div></StatCard>
        <StatCard><div className="label">Pending Meta</div><div className="value" style={{ color: "#0284c7" }}>{stats.pending}</div></StatCard>
        <StatCard><div className="label">Draft</div><div className="value" style={{ color: "#64748b" }}>{stats.draft}</div></StatCard>
        <StatCard><div className="label">Rejected</div><div className="value" style={{ color: "#ef4444" }}>{stats.rejected}</div></StatCard>
      </StatsBar>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <TableCard>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(row) => `${row.sequenceKey || "prospect"}-${row.stepDay}`}
            pagination={false}
            locale={{ emptyText: <Empty description={`No steps configured for ${activeSeq}`} /> }}
          />
        </Spin>
      </TableCard>

      {editing && (
        <TemplateEditor
          row={editing}
          sequenceKey={activeSeq}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await fetchAll();
          }}
        />
      )}

      {testing && (
        <TestSendDialog
          row={testing}
          sequenceKey={activeSeq}
          onClose={() => setTesting(null)}
        />
      )}
    </Page>
  );
}
